import { useEffect, useState } from 'react';
import { ShieldX, LogOut, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { tryInteractiveTokenRecovery } from '@/lib/msalConfig';

// Cooldown guards against infinite recovery loops when the redirect-back lands
// us right back on this page (e.g. AAD session truly dead AND the user dismissed
// the AAD sign-in screen). After the cooldown expires, the next 401 can trigger
// auto-recovery again. sessionStorage scopes this to the tab — closing and
// reopening resets it.
const RECOVERY_COOLDOWN_KEY = 'mo:auth-recovery-last-attempt';
const RECOVERY_COOLDOWN_MS = 60_000;

function inRecoveryCooldown(): boolean {
  try {
    const last = sessionStorage.getItem(RECOVERY_COOLDOWN_KEY);
    if (!last) return false;
    return Date.now() - parseInt(last, 10) < RECOVERY_COOLDOWN_MS;
  } catch {
    // sessionStorage unavailable (private mode, etc.) — fail open so the user
    // gets the manual UI instead of an auto-recovery loop they can't break out
    // of.
    return true;
  }
}

function markRecoveryAttempted(): void {
  try {
    sessionStorage.setItem(RECOVERY_COOLDOWN_KEY, Date.now().toString());
  } catch {
    /* sessionStorage unavailable — best effort only */
  }
}

interface NotAuthorizedPageProps {
  /**
   * - `not-provisioned`: backend confirmed (404 from /users/me) that the
   *   account isn't in the ApplicationUsers allow-list.
   * - `verification-failed`: backend couldn't be reached, returned an
   *   unexpected error, or the silent-token-acquisition hung past our
   *   verification timeout. Includes a Retry button so transient network
   *   blips don't require a full sign-out.
   */
  variant?: 'not-provisioned' | 'verification-failed';
}

/**
 * Shown when an Azure AD-authenticated principal cannot be confirmed as a
 * provisioned `ApplicationUsers` row. Backend 403s every protected endpoint in
 * this state, so rendering the dashboard would just produce a wall of "Failed
 * to load" errors. The page surfaces the rejected account email and always
 * provides a sign-out so the user can retry with a different identity.
 */
export function NotAuthorizedPage({ variant = 'not-provisioned' }: NotAuthorizedPageProps) {
  const { user, logout, retryMeCheck } = useAuth();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isAutoRecovering, setIsAutoRecovering] = useState(false);

  // Auto-recovery: on the verification-failed variant, attempt to refresh the
  // access token before showing the manual recovery UI. Two-tier strategy lives
  // in `tryInteractiveTokenRecovery`:
  //   1. silent forceRefresh — invisible if AAD session is alive
  //   2. acquireTokenRedirect — bounces through AAD, lands back on this URL
  // If both fail (or the cooldown is active because we already tried), the
  // existing Retry / Sign out buttons handle it. AuthGuard renders LoginPage
  // automatically if MSAL's isAuthenticated flips to false during recovery.
  useEffect(() => {
    if (variant !== 'verification-failed') return;
    if (inRecoveryCooldown()) return;

    let cancelled = false;
    setIsAutoRecovering(true);
    markRecoveryAttempted();

    tryInteractiveTokenRecovery()
      .then((outcome) => {
        if (cancelled) return;
        if (outcome === 'recovered') {
          // Silent forceRefresh worked. Force /users/me back through so
          // AuthGuard re-evaluates and mounts the dashboard.
          retryMeCheck();
        }
        // 'redirecting' → page is unloading; nothing to do.
        // 'failed' → fall through to the manual Retry / Sign out buttons.
        setIsAutoRecovering(false);
      })
      .catch(() => {
        if (!cancelled) setIsAutoRecovering(false);
      });

    return () => {
      cancelled = true;
    };
  }, [variant, retryMeCheck]);

  async function handleSignOut() {
    setIsSigningOut(true);
    try {
      await logout();
    } catch {
      setIsSigningOut(false);
    }
  }

  const title =
    variant === 'verification-failed' ? 'Couldn’t verify your access' : 'Access not authorized';
  const description =
    variant === 'verification-failed'
      ? isAutoRecovering
        ? 'Refreshing your sign-in… If this doesn’t complete, use Retry or sign out.'
        : 'We couldn’t confirm whether your account has access. This is usually a transient network or sign-in issue. Retry, or sign out and try again.'
      : 'Your account isn’t on the Migration Orchestrator users list. Contact your administrator to request access.';

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
            <ShieldX className="h-6 w-6 text-destructive" />
          </div>
          <CardTitle className="text-2xl">{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-3">
          {user?.email && (
            <p className="text-sm text-muted-foreground">
              Signed in as <span className="font-medium">{user.email}</span>
            </p>
          )}
          {variant === 'verification-failed' && (
            isAutoRecovering ? (
              <div className="flex items-center justify-center gap-2 py-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Reconnecting…
              </div>
            ) : (
              <Button onClick={retryMeCheck} size="lg" className="w-full">
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </Button>
            )
          )}
          <Button
            onClick={handleSignOut}
            disabled={isSigningOut}
            variant="outline"
            size="lg"
            className="w-full"
          >
            <LogOut className="mr-2 h-4 w-4" />
            {isSigningOut ? 'Signing out...' : 'Sign out and try a different account'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
